import { useState, useEffect } from 'react';
import { OnboardingStepHeader } from './OnboardingStepHeader';
import { OnboardingFooter } from './OnboardingFooter';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/lib/auth';
import { successToast, errorToast } from '@/components/ui/sonner';

interface IdentitySetupProps {
  onComplete: () => void;
  totalSteps?: number;
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 6,
  background: 'rgba(255,255,255,0.04)',
  color: '#fff',
  fontSize: 13,
  outline: 'none',
};

const labelStyle: React.CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  color: 'rgba(255,255,255,0.6)',
  marginBottom: 6,
  display: 'block',
};

export function IdentitySetup({ onComplete, totalSteps = 5 }: IdentitySetupProps) {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    jobTitle: '',
  });
  const [errors, setErrors] = useState<{ firstName?: string; lastName?: string }>({});

  useEffect(() => {
    if (!user) return;
    const prefill = async () => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('name, job_title')
        .eq('id', user.id)
        .single();

      let firstName = '';
      let lastName = '';
      let jobTitle = '';

      if (profile?.name) {
        const spaceIdx = profile.name.indexOf(' ');
        firstName = spaceIdx > 0 ? profile.name.slice(0, spaceIdx) : profile.name;
        lastName = spaceIdx > 0 ? profile.name.slice(spaceIdx + 1) : '';
      } else {
        const fullName = user.user_metadata?.full_name || user.user_metadata?.name || '';
        if (fullName) {
          const spaceIdx = fullName.indexOf(' ');
          firstName = spaceIdx > 0 ? fullName.slice(0, spaceIdx) : fullName;
          lastName = spaceIdx > 0 ? fullName.slice(spaceIdx + 1) : '';
        }
      }
      jobTitle = profile?.job_title || '';
      setFormData({ firstName, lastName, jobTitle });
    };
    prefill();
  }, [user]);

  const validateForm = () => {
    const newErrors: { firstName?: string; lastName?: string } = {};
    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = formData.firstName.trim() && formData.lastName.trim();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;
    if (!validateForm()) return;
    setLoading(true);
    try {
      const fullName = [formData.firstName.trim(), formData.lastName.trim()].filter(Boolean).join(' ');
      const { error } = await supabase
        .from('profiles')
        .update({ email: user.email, name: fullName || null, job_title: formData.jobTitle.trim() || null })
        .eq('id', user.id);
      if (error) throw error;
      successToast('Profile updated', 'Your identity has been saved.');
      onComplete();
    } catch (error) {
      console.error('Error updating profile:', error);
      errorToast('Error', 'Failed to save profile. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <OnboardingStepHeader
        step={1}
        totalSteps={totalSteps}
        title="Welcome. Let's get you set up."
        subtitle="Your name appears on proposal exports and in team collaboration."
      />

      <p style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)', marginBottom: 20 }}>Takes about 3 minutes.</p>

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>
              First Name <span style={{ color: '#C2541A' }}>*</span>
            </label>
            <input
              value={formData.firstName}
              onChange={(e) => {
                setFormData({ ...formData, firstName: e.target.value });
                if (errors.firstName) setErrors({ ...errors, firstName: undefined });
              }}
              placeholder="Jane"
              autoFocus
              style={inputStyle}
              onFocus={e => {
                e.currentTarget.style.borderColor = 'rgba(194,84,26,0.5)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }}
            />
            {errors.firstName && (
              <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{errors.firstName}</p>
            )}
          </div>
          <div>
            <label style={labelStyle}>
              Last Name <span style={{ color: '#C2541A' }}>*</span>
            </label>
            <input
              value={formData.lastName}
              onChange={(e) => {
                setFormData({ ...formData, lastName: e.target.value });
                if (errors.lastName) setErrors({ ...errors, lastName: undefined });
              }}
              placeholder="Smith"
              style={inputStyle}
              onFocus={e => {
                e.currentTarget.style.borderColor = 'rgba(194,84,26,0.5)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
              }}
              onBlur={e => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
              }}
            />
            {errors.lastName && (
              <p style={{ fontSize: 11, color: '#ef4444', marginTop: 4 }}>{errors.lastName}</p>
            )}
          </div>
        </div>

        <div>
          <label style={labelStyle}>
            Job Title <span style={{ color: 'rgba(255,255,255,0.3)', fontWeight: 400 }}>(optional)</span>
          </label>
          <input
            value={formData.jobTitle}
            onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
            placeholder="e.g., Proposal Manager, BD Director"
            style={inputStyle}
            onFocus={e => {
              e.currentTarget.style.borderColor = 'rgba(194,84,26,0.5)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
            }}
            onBlur={e => {
              e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
              e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
            }}
          />
        </div>

        <OnboardingFooter loading={loading} disabled={!isFormValid} />
      </form>
    </>
  );
}
